<template>
  <div class="image-crop" :class="statusClass">
    <!-- Estado inicial / sin imagen -->
    <label
      v-if="!imageUrl"
      class="image-crop__dropzone"
      @dragover.prevent
      @drop.prevent="onDrop"
    >
      <span class="image-crop__badge">
        <i class="uil uil-vector-square"></i>
        {{ badgeLabel }}
      </span>

      <i :class="iconClass" class="upload-icon"></i>
      <span class="upload-label">{{ label }}</span>
      <span class="upload-hint">{{ hint }}</span>
      <span class="upload-hint upload-hint--accept">{{ acceptedHint }}</span>

      <span v-if="errorMessage" class="status status--err">
        <i class="uil uil-exclamation-triangle"></i>
        {{ errorMessage }}
      </span>

      <input
        :accept="acceptAttr"
        type="file"
        ref="fileupload"
        :name="name"
        class="hidden"
        @change="onFileChange"
      />
    </label>

    <!-- Vista de recorte -->
    <div v-else-if="!confirmedBase64" class="image-crop__editor">
      <span class="image-crop__badge">
        <i class="uil uil-crop-alt"></i>
        {{ badgeLabel }}
      </span>

      <i
        class="uil uil-times reset-btn"
        @click.stop="reset"
        title="Quitar imagen"
      ></i>

      <p class="editor-hint">
        <i class="uil uil-arrows-h-alt"></i>
        Ajusta el área de la {{ selectionLabel }} y confirma.
      </p>

      <div
        class="crop-stage"
        ref="stageRef"
        :style="{ maxHeight: stageMaxHeightCss, minHeight: '240px' }"
      >
        <img
          ref="imgRef"
          :src="imageUrl"
          class="crop-img"
          :style="{ maxHeight: stageMaxHeightCss }"
          @load="onImageLoad"
          draggable="false"
        />

        <div
          v-if="imageReady"
          class="crop-overlay"
          :style="overlayStyle"
        ></div>

        <div
          v-if="imageReady"
          class="crop-selection"
          :style="selectionStyle"
          @pointerdown.stop="startDrag($event, 'move')"
        >
          <span
            v-for="h in handles"
            :key="h"
            class="crop-handle"
            :class="`crop-handle--${h}`"
            @pointerdown.stop="startDrag($event, h)"
          ></span>
        </div>
      </div>

      <div class="editor-actions">
        <button type="button" class="btn btn--ghost" @click.stop="reset">
          <i class="uil uil-redo"></i>
          Cambiar imagen
        </button>
        <button
          type="button"
          class="btn btn--primary"
          :disabled="!imageReady"
          @click.stop="confirmCrop"
        >
          <i class="uil uil-check"></i>
          Confirmar {{ selectionLabel }}
        </button>
      </div>

      <span v-if="errorMessage" class="status status--err">
        <i class="uil uil-exclamation-triangle"></i>
        {{ errorMessage }}
      </span>
    </div>

    <!-- Vista de resultado -->
    <div v-else class="image-crop__result">
      <span class="image-crop__badge image-crop__badge--ok">
        <i class="uil uil-check-circle"></i>
        {{ resultLabel }}
      </span>

      <i
        class="uil uil-times reset-btn"
        @click.stop="reset"
        title="Quitar imagen"
      ></i>

      <img :src="confirmedPreview" class="result-img" />

      <div class="editor-actions">
        <button type="button" class="btn btn--ghost" @click.stop="editAgain">
          <i class="uil uil-crop-alt"></i>
          Reajustar
        </button>
        <button type="button" class="btn btn--ghost" @click.stop="reset">
          <i class="uil uil-redo"></i>
          Cambiar imagen
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, ref, watch } from "vue";

export default {
  name: "AppImageCrop",
  props: {
    modelValue: { type: String, default: "" },
    icon: { type: String, default: "image-upload" },
    label: { type: String, default: "Cargar imagen" },
    hint: { type: String, default: "Selecciona el área a recortar" },
    name: { type: String, default: "image-crop" },
    accept: { type: String, default: "" },
    badgeLabel: { type: String, default: "CROP" },
    selectionLabel: { type: String, default: "selección" },
    resultLabel: { type: String, default: "Recorte listo" },
    maxSizeMB: { type: Number, default: 5 },
    outputType: {
      type: String,
      default: "image/png",
      validator: (v) => ["image/png", "image/jpeg", "image/webp"].includes(v),
    },
    outputQuality: { type: Number, default: 0.92 },
    aspectRatio: { type: Number, default: 0 }, // 0 = libre
    minCropSize: { type: Number, default: 40 },
    maxOutputWidth: { type: Number, default: 1024 },
    includeDataUrlPrefix: { type: Boolean, default: false },
    stageMaxHeight: { type: [Number, String], default: 600 },
  },
  emits: ["update:modelValue", "crop", "error", "reset", "file-selected"],
  setup(props, { emit }) {
    const fileupload = ref(null);
    const imgRef = ref(null);
    const stageRef = ref(null);

    const imageUrl = ref(null);
    const imageReady = ref(false);
    const errorMessage = ref("");
    const confirmedBase64 = ref("");
    const confirmedPreview = ref("");
    const originalFile = ref(null);

    // Caja de selección (en coords del DOM relativo al <img>)
    const sel = ref({ x: 0, y: 0, w: 0, h: 0 });
    const imgBox = ref({ w: 0, h: 0, naturalW: 0, naturalH: 0 });

    const handles = [
      "n",
      "s",
      "e",
      "w",
      "ne",
      "nw",
      "se",
      "sw",
    ];

    const FILE_TYPE_CONFIG = {
      accept: "image/png,image/jpg,image/jpeg,image/webp",
      mimeTypes: ["image/png", "image/jpg", "image/jpeg", "image/webp"],
      extensions: ["png", "jpg", "jpeg", "webp"],
    };

    const acceptAttr = computed(() => props.accept || FILE_TYPE_CONFIG.accept);
    const acceptedHint = computed(
      () => `JPG, PNG, WEBP (máx. ${props.maxSizeMB}MB)`
    );
    const iconClass = computed(() => `uil uil-${props.icon}`);
    const stageMaxHeightCss = computed(() =>
      typeof props.stageMaxHeight === "number"
        ? `${props.stageMaxHeight}px`
        : props.stageMaxHeight
    );

    const statusClass = computed(() => ({
      "is-empty": !imageUrl.value,
      "is-editing": imageUrl.value && !confirmedBase64.value,
      "is-done": !!confirmedBase64.value,
      "is-error": !!errorMessage.value,
    }));

    const overlayStyle = computed(() => ({
      clipPath: `polygon(
        0 0, 100% 0, 100% 100%, 0 100%, 0 0,
        ${sel.value.x}px ${sel.value.y}px,
        ${sel.value.x}px ${sel.value.y + sel.value.h}px,
        ${sel.value.x + sel.value.w}px ${sel.value.y + sel.value.h}px,
        ${sel.value.x + sel.value.w}px ${sel.value.y}px,
        ${sel.value.x}px ${sel.value.y}px
      )`,
    }));

    const selectionStyle = computed(() => ({
      transform: `translate(${sel.value.x}px, ${sel.value.y}px)`,
      width: sel.value.w + "px",
      height: sel.value.h + "px",
    }));

    const validateFile = (file) => {
      if (!file) return "No se seleccionó ningún archivo";
      const mime = (file.type || "").toLowerCase();
      const ext = (file.name?.split(".").pop() || "").toLowerCase();
      const okType =
        FILE_TYPE_CONFIG.mimeTypes.includes(mime) ||
        FILE_TYPE_CONFIG.extensions.includes(ext);
      if (!okType) {
        return `Formato no permitido. Solo ${FILE_TYPE_CONFIG.extensions
          .map((e) => e.toUpperCase())
          .join(", ")}.`;
      }
      const maxBytes = props.maxSizeMB * 1024 * 1024;
      if (file.size > maxBytes) {
        return `La imagen excede el tamaño máximo permitido (${props.maxSizeMB}MB).`;
      }
      return null;
    };

    const open = () => {
      if (fileupload.value) fileupload.value.click();
    };

    const onFileChange = (e) => {
      const file = e.target?.files?.[0];
      handleFile(file);
    };

    const onDrop = (e) => {
      const file = e.dataTransfer?.files?.[0];
      handleFile(file);
    };

    const handleFile = (file) => {
      errorMessage.value = "";
      const validation = validateFile(file);
      if (validation) {
        errorMessage.value = validation;
        emit("error", new Error(validation));
        return;
      }
      revokeObjectUrl();
      originalFile.value = file;
      imageUrl.value = URL.createObjectURL(file);
      imageReady.value = false;
      confirmedBase64.value = "";
      confirmedPreview.value = "";
      emit("file-selected", file);
    };

    const onImageLoad = () => {
      const img = imgRef.value;
      if (!img) return;
      imgBox.value = {
        w: img.clientWidth,
        h: img.clientHeight,
        naturalW: img.naturalWidth,
        naturalH: img.naturalHeight,
      };
      // Selección inicial centrada (60% del lado más corto)
      const base = Math.min(img.clientWidth, img.clientHeight) * 0.6;
      let w = base;
      let h = base;
      if (props.aspectRatio > 0) {
        if (props.aspectRatio >= 1) {
          w = Math.min(img.clientWidth * 0.8, base * props.aspectRatio);
          h = w / props.aspectRatio;
        } else {
          h = Math.min(img.clientHeight * 0.8, base / props.aspectRatio);
          w = h * props.aspectRatio;
        }
      }
      sel.value = {
        x: (img.clientWidth - w) / 2,
        y: (img.clientHeight - h) / 2,
        w,
        h,
      };
      imageReady.value = true;
    };

    // ===== Drag & resize =====
    let dragData = null;

    const startDrag = (event, mode) => {
      event.preventDefault();
      const target = event.currentTarget;
      try {
        target.setPointerCapture(event.pointerId);
      } catch (_) {
        // ignored
      }
      dragData = {
        mode,
        startX: event.clientX,
        startY: event.clientY,
        orig: { ...sel.value },
        target,
        pointerId: event.pointerId,
      };
      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
    };

    const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

    const onPointerMove = (e) => {
      if (!dragData) return;
      const dx = e.clientX - dragData.startX;
      const dy = e.clientY - dragData.startY;
      const { mode, orig } = dragData;
      const bounds = imgBox.value;
      let { x, y, w, h } = orig;
      const min = props.minCropSize;

      if (mode === "move") {
        x = clamp(orig.x + dx, 0, bounds.w - orig.w);
        y = clamp(orig.y + dy, 0, bounds.h - orig.h);
      } else {
        if (mode.includes("e")) w = clamp(orig.w + dx, min, bounds.w - orig.x);
        if (mode.includes("s")) h = clamp(orig.h + dy, min, bounds.h - orig.y);
        if (mode.includes("w")) {
          const newX = clamp(orig.x + dx, 0, orig.x + orig.w - min);
          w = orig.w + (orig.x - newX);
          x = newX;
        }
        if (mode.includes("n")) {
          const newY = clamp(orig.y + dy, 0, orig.y + orig.h - min);
          h = orig.h + (orig.y - newY);
          y = newY;
        }

        if (props.aspectRatio > 0) {
          // Mantener aspect ratio en función del eje principal del handle
          if (mode === "e" || mode === "w") {
            h = w / props.aspectRatio;
            if (y + h > bounds.h) h = bounds.h - y;
            w = h * props.aspectRatio;
          } else if (mode === "n" || mode === "s") {
            w = h * props.aspectRatio;
            if (x + w > bounds.w) w = bounds.w - x;
            h = w / props.aspectRatio;
          } else {
            // Esquinas: usar el cambio máximo
            const ratio = props.aspectRatio;
            if (w / h > ratio) w = h * ratio;
            else h = w / ratio;
            if (mode.includes("w")) x = orig.x + orig.w - w;
            if (mode.includes("n")) y = orig.y + orig.h - h;
          }
        }
      }

      sel.value = { x, y, w, h };
    };

    const onPointerUp = () => {
      if (dragData?.target && dragData.pointerId != null) {
        try {
          dragData.target.releasePointerCapture(dragData.pointerId);
        } catch (_) {
          // ignored
        }
      }
      dragData = null;
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };

    // ===== Confirmar recorte =====
    const confirmCrop = () => {
      const img = imgRef.value;
      if (!img || !imageReady.value) return;

      const scaleX = imgBox.value.naturalW / imgBox.value.w;
      const scaleY = imgBox.value.naturalH / imgBox.value.h;

      const sx = Math.round(sel.value.x * scaleX);
      const sy = Math.round(sel.value.y * scaleY);
      const sw = Math.round(sel.value.w * scaleX);
      const sh = Math.round(sel.value.h * scaleY);

      let outW = sw;
      let outH = sh;
      if (props.maxOutputWidth > 0 && outW > props.maxOutputWidth) {
        const ratio = props.maxOutputWidth / outW;
        outW = Math.round(outW * ratio);
        outH = Math.round(outH * ratio);
      }

      const canvas = document.createElement("canvas");
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext("2d");

      if (props.outputType === "image/jpeg") {
        ctx.fillStyle = "#FFFFFF";
        ctx.fillRect(0, 0, outW, outH);
      }

      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);

      const dataUrl = canvas.toDataURL(props.outputType, props.outputQuality);
      const base64 = dataUrl.split(",")[1] || "";

      confirmedBase64.value = base64;
      confirmedPreview.value = dataUrl;

      const value = props.includeDataUrlPrefix ? dataUrl : base64;
      emit("update:modelValue", value);
      emit("crop", {
        base64,
        dataUrl,
        mimeType: props.outputType,
        width: outW,
        height: outH,
        sourceWidth: sw,
        sourceHeight: sh,
      });
    };

    const editAgain = () => {
      confirmedBase64.value = "";
      confirmedPreview.value = "";
      emit("update:modelValue", "");
    };

    const revokeObjectUrl = () => {
      if (imageUrl.value && imageUrl.value.startsWith("blob:")) {
        try {
          URL.revokeObjectURL(imageUrl.value);
        } catch (_) {
          // ignored
        }
      }
    };

    const reset = () => {
      revokeObjectUrl();
      imageUrl.value = null;
      imageReady.value = false;
      confirmedBase64.value = "";
      confirmedPreview.value = "";
      originalFile.value = null;
      errorMessage.value = "";
      sel.value = { x: 0, y: 0, w: 0, h: 0 };
      if (fileupload.value) fileupload.value.value = "";
      emit("update:modelValue", "");
      emit("reset");
    };

    // Si el v-model se limpia desde fuera, reseteamos
    watch(
      () => props.modelValue,
      (val) => {
        if (!val && confirmedBase64.value) {
          reset();
        }
      }
    );

    onBeforeUnmount(() => {
      revokeObjectUrl();
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    });

    return {
      fileupload,
      imgRef,
      stageRef,
      imageUrl,
      imageReady,
      errorMessage,
      confirmedBase64,
      confirmedPreview,
      sel,
      handles,
      iconClass,
      acceptAttr,
      acceptedHint,
      stageMaxHeightCss,
      statusClass,
      overlayStyle,
      selectionStyle,
      open,
      onFileChange,
      onDrop,
      onImageLoad,
      startDrag,
      confirmCrop,
      editAgain,
      reset,
    };
  },
};
</script>

<style lang="scss" scoped>
.image-crop {
  @apply w-full;
  @apply relative;

  &__dropzone {
    @apply rounded-lg;
    @apply border-2;
    @apply border-dashed;
    @apply border-purple-300;
    @apply w-full;
    @apply h-auto;
    @apply cursor-pointer;
    @apply flex;
    @apply justify-center;
    @apply items-center;
    @apply py-4;
    @apply px-3;
    @apply transition-all;
    @apply flex-col;
    @apply text-center;
    @apply relative;
    @apply overflow-hidden;
    background: linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%);
    min-height: 140px;

    &:hover {
      @apply border-purple-500;
      background: linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%);
      box-shadow: 0 4px 12px rgba(139, 92, 246, 0.15);
    }
  }

  &__editor,
  &__result {
    @apply rounded-lg;
    @apply border-2;
    @apply border-dashed;
    @apply border-purple-300;
    @apply w-full;
    @apply p-3;
    @apply flex;
    @apply flex-col;
    @apply items-center;
    @apply gap-3;
    @apply relative;
    background: linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%);
  }

  &.is-done &__result {
    @apply border-green-400;
    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  }

  &.is-error &__dropzone {
    @apply border-red-400;
    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  }

  &__badge {
    @apply absolute;
    @apply top-2;
    @apply left-2;
    @apply text-xs;
    @apply font-bold;
    @apply px-2;
    @apply py-0.5;
    @apply rounded-full;
    @apply text-white;
    @apply flex;
    @apply items-center;
    @apply gap-1;
    background: linear-gradient(90deg, #8b5cf6 0%, #6366f1 100%);
    box-shadow: 0 2px 6px rgba(139, 92, 246, 0.35);
    z-index: 3;

    &--ok {
      background: linear-gradient(90deg, #10b981 0%, #059669 100%);
      box-shadow: 0 2px 6px rgba(16, 185, 129, 0.35);
    }

    i {
      @apply text-white;
      @apply text-xs;
      @apply m-0;
    }
  }

  .reset-btn {
    @apply absolute;
    @apply right-2;
    @apply top-2;
    @apply text-base;
    @apply text-purple-600;
    @apply bg-white;
    @apply rounded-full;
    @apply w-6;
    @apply h-6;
    @apply flex;
    @apply items-center;
    @apply justify-center;
    @apply shadow;
    @apply m-0;
    @apply cursor-pointer;
    z-index: 3;

    &:hover {
      @apply text-red-500;
    }
  }

  .upload-icon {
    @apply text-purple-500;
    @apply text-4xl;
    @apply transition-all;
    @apply inline-block;
    @apply mb-1;
  }

  .upload-label {
    @apply text-purple-700;
    @apply text-sm;
    @apply font-semibold;
    @apply mt-1;
  }

  .upload-hint {
    @apply text-purple-400;
    @apply text-xs;
    @apply mt-1;
    @apply italic;
  }

  .editor-hint {
    @apply text-purple-700;
    @apply text-xs;
    @apply font-semibold;
    @apply flex;
    @apply items-center;
    @apply gap-1;
    @apply mt-4;
    @apply mb-0;

    i {
      @apply text-sm;
      @apply m-0;
      @apply text-purple-500;
    }
  }

  .crop-stage {
    @apply relative;
    @apply w-full;
    @apply rounded;
    @apply overflow-hidden;
    @apply bg-gray-900;
    @apply flex;
    @apply justify-center;
    @apply items-center;
    user-select: none;
    touch-action: none;
  }

  .crop-img {
    @apply block;
    max-width: 100%;
    width: auto;
    height: auto;
    pointer-events: none;
    user-select: none;
  }

  .crop-overlay {
    @apply absolute;
    @apply inset-0;
    background: rgba(15, 23, 42, 0.55);
    pointer-events: none;
  }

  .crop-selection {
    @apply absolute;
    @apply top-0;
    @apply left-0;
    border: 2px dashed #a78bfa;
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.6) inset;
    cursor: move;
    touch-action: none;
  }

  .crop-handle {
    @apply absolute;
    @apply bg-white;
    @apply rounded-sm;
    width: 12px;
    height: 12px;
    border: 2px solid #8b5cf6;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);

    &--n {
      top: -7px;
      left: 50%;
      transform: translateX(-50%);
      cursor: ns-resize;
    }
    &--s {
      bottom: -7px;
      left: 50%;
      transform: translateX(-50%);
      cursor: ns-resize;
    }
    &--e {
      right: -7px;
      top: 50%;
      transform: translateY(-50%);
      cursor: ew-resize;
    }
    &--w {
      left: -7px;
      top: 50%;
      transform: translateY(-50%);
      cursor: ew-resize;
    }
    &--ne {
      top: -7px;
      right: -7px;
      cursor: nesw-resize;
    }
    &--nw {
      top: -7px;
      left: -7px;
      cursor: nwse-resize;
    }
    &--se {
      bottom: -7px;
      right: -7px;
      cursor: nwse-resize;
    }
    &--sw {
      bottom: -7px;
      left: -7px;
      cursor: nesw-resize;
    }
  }

  .editor-actions {
    @apply flex;
    @apply gap-2;
    @apply justify-end;
    @apply w-full;
    @apply flex-wrap;
  }

  .btn {
    @apply rounded-md;
    @apply px-3;
    @apply py-1.5;
    @apply text-xs;
    @apply font-semibold;
    @apply flex;
    @apply items-center;
    @apply gap-1;
    @apply transition-all;
    @apply cursor-pointer;
    border: 1px solid transparent;

    i {
      @apply text-sm;
      @apply m-0;
    }

    &--primary {
      @apply text-white;
      background: linear-gradient(90deg, #8b5cf6 0%, #6366f1 100%);
      box-shadow: 0 2px 6px rgba(139, 92, 246, 0.35);

      i {
        @apply text-white;
      }

      &:hover:not(:disabled) {
        filter: brightness(1.05);
      }

      &:disabled {
        @apply opacity-50;
        @apply cursor-not-allowed;
      }
    }

    &--ghost {
      @apply text-purple-700;
      @apply bg-white;
      border-color: #c4b5fd;

      i {
        @apply text-purple-600;
      }

      &:hover {
        @apply bg-purple-50;
      }
    }
  }

  .result-img {
    @apply max-w-full;
    @apply max-h-60;
    @apply rounded;
    @apply mt-6;
    @apply bg-white;
    @apply p-2;
    @apply shadow;
  }

  .status {
    @apply text-xs;
    @apply font-semibold;
    @apply mt-2;
    @apply flex;
    @apply items-center;
    @apply gap-1;

    i {
      @apply text-base;
      @apply m-0;
    }

    &--err {
      @apply text-red-600;
      i {
        @apply text-red-600;
      }
    }
  }
}
</style>
